import type { Attendee, AttendeeSide, ConfirmedPerson } from '@sl/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Who was actually on the call.
 *
 * The call platform's display names are not identities — a designer speaks
 * from the marketing manager's account, and the bride sits beside her mother
 * and never says a word — so this is where a human settles it. Every speaker
 * label in the transcript gets a real name, a side and a role, and anyone who
 * was present but silent is added by hand, because no transcript will ever
 * produce them.
 *
 * What comes out of here is CONFIRMED FACT as far as the generator is
 * concerned: it stops hedging about these people entirely.
 */

const SIDES: AttendeeSide[] = ['HOUSE', 'CLIENT', 'OTHER'];

interface Row {
  /** The transcript label this person speaks under; '' for a silent person. */
  speakerLabel: string;
  name: string;
  side: AttendeeSide;
  title: string;
  silent: boolean;
}

/** Seeds the form from what the meeting already knows, so a second pass over
 * the dialog is an edit rather than a retype. */
export function seedRows(speakers: string[], attendees: Attendee[]): Row[] {
  const byLabel = new Map(
    attendees.filter((a) => a.speakerLabel).map((a) => [a.speakerLabel as string, a]),
  );
  const byName = new Map(
    attendees.filter((a) => a.externalName).map((a) => [a.externalName as string, a]),
  );

  const rows: Row[] = speakers.map((label) => {
    const match = byLabel.get(label) ?? byName.get(label);
    return {
      speakerLabel: label,
      name: match?.externalName ?? label,
      side: match?.side ?? 'HOUSE',
      title: match?.title ?? match?.roleInMeeting ?? '',
      silent: false,
    };
  });

  // Anyone already on the meeting who does not speak in the transcript — the
  // silent people a previous pass added — is kept rather than quietly dropped.
  const claimed = new Set(rows.map((r) => r.name));
  for (const a of attendees) {
    const name = a.externalName ?? '';
    if (!name || claimed.has(name) || speakers.includes(name)) continue;
    rows.push({
      speakerLabel: '',
      name,
      side: a.side ?? 'OTHER',
      title: a.title ?? a.roleInMeeting ?? '',
      silent: a.silent,
    });
  }
  return rows;
}

export function rowsToPeople(rows: Row[]): ConfirmedPerson[] {
  return rows
    .filter((r) => r.name.trim())
    .map((r) => ({
      speakerLabel: r.speakerLabel.trim() || null,
      name: r.name.trim(),
      side: r.side,
      title: r.title.trim() || null,
      silent: r.silent,
    }));
}

export function ConfirmPeopleDialog({
  speakers,
  attendees,
  realClientName,
  preparedByOverride,
  busy,
  onCancel,
  onSave,
}: {
  speakers: string[];
  attendees: Attendee[];
  realClientName: string | null;
  preparedByOverride: string | null;
  busy: boolean;
  onCancel: () => void;
  onSave: (input: {
    people: ConfirmedPerson[];
    realClientName: string | null;
    preparedByOverride: string | null;
  }) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Row[]>(() => seedRows(speakers, attendees));
  const [client, setClient] = useState(realClientName ?? '');
  const [preparedBy, setPreparedBy] = useState(preparedByOverride ?? '');

  const patch = (index: number, next: Partial<Row>): void =>
    setRows((current) => current.map((r, i) => (i === index ? { ...r, ...next } : r)));

  const addPerson = (): void =>
    setRows((current) => [
      ...current,
      { speakerLabel: '', name: '', side: 'CLIENT', title: '', silent: true },
    ]);

  return (
    <div className="dialog-backdrop" onClick={onCancel} role="presentation">
      <div
        className="dialog dialog-wide"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <h2>{t('people.title')}</h2>
        <p className="muted small">{t('people.hint')}</p>

        {rows.map((row, i) => (
          <div className="person-row" key={`${row.speakerLabel}-${i}`}>
            <div className="person-row-head">
              {row.speakerLabel ? (
                <span className="person-label" dir="auto">
                  {row.speakerLabel}
                </span>
              ) : (
                <span className="person-label muted">{t('people.notInTranscript')}</span>
              )}
              <button
                type="button"
                className="link danger"
                onClick={() => setRows((c) => c.filter((_, j) => j !== i))}
              >
                {t('mom.remove')}
              </button>
            </div>

            <label className="mom-field">
              <span className="mom-field-label">{t('people.name')}</span>
              <input
                dir="auto"
                value={row.name}
                placeholder={row.speakerLabel}
                onChange={(e) => patch(i, { name: e.target.value })}
              />
            </label>

            <div className="person-row-grid">
              <label className="mom-field">
                <span className="mom-field-label">{t('people.side')}</span>
                <select
                  value={row.side}
                  onChange={(e) => patch(i, { side: e.target.value as AttendeeSide })}
                >
                  {SIDES.map((side) => (
                    <option key={side} value={side}>
                      {t(`people.sides.${side}`)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="mom-field">
                <span className="mom-field-label">{t('people.role')}</span>
                <input
                  dir="auto"
                  value={row.title}
                  placeholder={t('people.rolePlaceholder') as string}
                  onChange={(e) => patch(i, { title: e.target.value })}
                />
              </label>
            </div>

            <label className="mom-inline-check">
              <input
                type="checkbox"
                checked={row.silent}
                onChange={(e) => patch(i, { silent: e.target.checked })}
              />
              {t('people.silent')}
            </label>
          </div>
        ))}

        <button type="button" className="secondary" onClick={addPerson}>
          {t('people.addPerson')}
        </button>

        <label className="mom-field">
          <span className="mom-field-label">{t('people.realClient')}</span>
          <input
            dir="auto"
            value={client}
            placeholder={t('people.realClientPlaceholder') as string}
            onChange={(e) => setClient(e.target.value)}
          />
        </label>

        <label className="mom-field">
          <span className="mom-field-label">{t('people.preparedBy')}</span>
          <input
            dir="auto"
            value={preparedBy}
            placeholder={t('people.preparedByPlaceholder') as string}
            onChange={(e) => setPreparedBy(e.target.value)}
          />
        </label>

        <div className="row">
          <button
            type="button"
            className="gold"
            disabled={busy}
            onClick={() =>
              onSave({
                people: rowsToPeople(rows),
                realClientName: client.trim() || null,
                preparedByOverride: preparedBy.trim() || null,
              })
            }
          >
            {t('common.save')}
          </button>
          <button type="button" className="secondary" onClick={onCancel}>
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}
